import React, { useState } from "react";
import { UploadCloud, X } from "lucide-react";
import { useAppDispatch } from "src/hooks/redux";
import { getUploadStatus, ragUploadPdfs } from "./action"; // adjust path as needed
import LoadingUI from "../ai-agent/LoadingUi";
import pdot from "src/assets/images/icons/p-dot.svg";
import { getTimeCurrent } from "src/utils/helpers";

interface UploadedFile {
    file: File;
    status: "Completed" | "Uploading";
}

const getFileTypeIcon = (extension: string) => {
    switch (extension) {
        case "jpg":
        case "png":
        case "jpeg":
            return (
                <div className="h-6 w-6 bg-blue-500 rounded text-white flex items-center justify-center">
                    🖼️
                </div>
            );
        case "mp4":
        case "mov":
        case "avi":
            return (
                <div className="h-4 w-4 bg-green-500 rounded text-white flex items-center justify-center">
                    🎥
                </div>
            );
        case "pdf":
            return (
                <div className="h-4 w-4 bg-gray-500 rounded text-white flex items-center justify-center">
                    📄
                </div>
            );
        default:
            return (
                <div className="h-4 w-4 bg-gray-500 rounded text-white flex items-center justify-center">
                    📄
                </div>
            );
    }
};

const getFileExtension = (name: string) => {
    if (!name) {
        return ""
    }
    const parts = name.split(".");
    return parts.length > 1 ? parts?.pop()?.toLowerCase() || "" : "unknown";
};

interface Props {
    threadId: string | null;
    userId: string | null | undefined;
    setFileUploaded: (value: boolean) => void;
    fileuploaded?: boolean;
}

const FileUploadSection: React.FC<Props> = ({ threadId, userId, setFileUploaded, fileuploaded }) => {
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const dispatch = useAppDispatch();

    const allowedTypes = [
        "application/pdf",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer.files);
        handleFiles(droppedFiles);
    };

    const handleFiles = (selectedFiles: File[]) => {
        const validFiles = selectedFiles.filter((file) =>
            allowedTypes.includes(file.type)
        );

        const invalidFiles = selectedFiles.filter(
            (file) => !allowedTypes.includes(file.type)
        );

        if (invalidFiles.length > 0) {
            setError("Only PDF and Excel files are allowed.");
        } else {
            setError(null);
        }

        const updatedFiles: UploadedFile[] = validFiles.map((file) => ({
            file,
            status: "Completed",
        }));

        setFiles((prev) => [...prev, ...updatedFiles]);
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            handleFiles(Array.from(e.target.files));
        }
    };

    const removeFile = (index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleFileUpload = async () => {
        if (!files.length || !threadId) return;

        const pdfFiles = files.filter(f => f.file.type === "application/pdf").map(f => f.file);
        const excelFiles = files.filter(f =>
            f.file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
            f.file.type === "application/vnd.ms-excel"
        ).map(f => f.file);

        setLoading(true);

        try {
            const uploadPromises: Promise<any>[] = [];

            if (pdfFiles.length) {
                const pdfFormData = new FormData();
                pdfFiles.forEach(file => {
                    pdfFormData.append("files", file);
                });

                const pdfUpload = dispatch(
                    ragUploadPdfs({
                        formdata: pdfFormData,
                        userId: userId || "",
                        threadId,
                    })
                ).unwrap();

                uploadPromises.push(pdfUpload);
            }

            if (excelFiles.length) {
                const excelFormData = new FormData();
                excelFiles.forEach(file => {
                    excelFormData.append("file", file);
                });

                const excelUpload = dispatch(
                    ragUploadPdfs({
                        formdata: excelFormData,
                        userId: userId || "",
                        threadId,
                        isExcel: true,
                    })
                ).unwrap();

                uploadPromises.push(excelUpload);
            }

            const results = await Promise.all(uploadPromises);
            const jobId = results[0]?.data?.job_id;
            const allSuccessful = results.every(res => res?.data);

            if (allSuccessful && jobId) {
                await pollUploadStatus(jobId); // ⬅ Start polling after upload success
            }


        } catch (err) {
            console.error("Upload error:", err);
            alert("Error uploading files.");
        } finally {
            setLoading(false);
        }
    };

    const pollUploadStatus = async (jobId: string) => {
        try {
            let status = "";
            while (status !== "completed") {
                const res = await getUploadFinalStatus(jobId);
                status = res?.data?.status?.toLowerCase();
                if (status === "done") {
                    setFileUploaded(true);
                    break;
                }
                // Wait for 5 seconds before the next check
                await new Promise(resolve => setTimeout(resolve, 30000));
            }
        } catch (error) {
            console.error("Polling error:", error);
        }
    };

    const getUploadFinalStatus = async (jobId: string) => {
        const response = await dispatch(
            getUploadStatus({ job_id: jobId })
        ).unwrap();
        return response;
    };

    return (
        <>
            <div className="shadow-md border border-gray-200 bg-white flex-col rounded-2xl rounded-bl-none flex  px-4 py-2 gap-2 relative  ">
                <div className="flex items-center justify-between border-b border-b-gray-300 pb-1">
                    <div className="flex items-center space-x-1">
                        <img src={pdot} alt={"Pn"} className="h-4 w-4 " />
                        <h2 className="text-gray-900 font-semibold">Rag Chatbot</h2>
                    </div>
                    <span className="relative before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-[2px] before:h-4 before:bg-gray-200 before:mr-2 pl-2 text-gray-400 text-sm">
                        {getTimeCurrent()}
                    </span>
                </div>

                <p className="m-0 text-[#5f6465]">Hello there! I'm here to assist you with gathering all the necessary information for your company report. Let's get started!</p>

                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800">
                            Upload files
                        </h2>
                        <p className="text-xs text-gray-500 mt-0">Upload PDF and Excel files</p>
                        {/* {loading && ( */}
                        {/* )} */}
                    </div>

                    <div
                        onDrop={handleDrop}
                        onDragOver={(e) => e.preventDefault()}
                        className="border-2 border-dashed border-[#FFA300] rounded-xl p-1 text-center bg-white cursor-pointer hover:bg-orange-50 transition-all duration-200"
                    >
                        <div className="flex items-center gap-1">
                            <UploadCloud className="text-black w-4 h-4" />
                            <div className="text-start">
                                <p className="font-medium text-base">Choose a file or drag & drop it here</p>
                                <p className="text-xs text-gray-500">
                                    Upload PDF and Excel files, up to 200 MB per file
                                </p>
                            </div>
                            <label className="inline-flex whitespace-nowrap p-1 border border-[#FFA300] text-xs rounded-lg cursor-pointer hover:bg-orange-50 text-[#373D3F]">
                                Browse File
                                <input
                                    type="file"
                                    accept=".pdf, .xls, .xlsx"
                                    className="hidden"
                                    onChange={handleFileInput}
                                    multiple
                                />
                            </label>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="text-sm text-red-600 bg-red-100 px-3 py-2 rounded-md">
                        {error}
                    </div>
                )}

                <div className="flex flex-wrap items-center gap-1">
                    {files.map((f, index) => (
                        <>
                            <div className="flex flex-wrap items-center justify-end">
                                <div className="bg-foundationOrange-100 border-secondary-500 p-1 rounded-2xl rounded-br-none">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center">
                                            {getFileExtension(f.file.name) ? getFileTypeIcon(getFileExtension(f.file.name)) : null}
                                            <div className="ml-2">
                                                <p className="text-sm font-medium text-gray-800">{f.file.name}</p>
                                                <p className="text-xs text-gray-500">{getFileExtension(f.file.name)}</p>
                                            </div>
                                        </div>
                                        <div
                                            className="border-2 border-[#FB3449] rounded-full p-0.5 bg-gray-100"
                                            onClick={() => removeFile(index)}
                                            role="button"
                                        >
                                            <X size={12} color="#FB3449" />
                                            {/* <CrossIcon  /> */}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    ))}
                </div>

                <div className="text-right">
                    <button
                        onClick={handleFileUpload}
                        disabled={loading}
                        className={`p-1 bg-white px-2 border border-[#FFA300] text-sm rounded-lg cursor-pointer ${loading ? "opacity-50 cursor-not-allowed" : "hover:bg-orange-50"
                            } text-[#373D3F]`}
                    >
                        {loading ? "Uploading..." : "Upload and Process Files"}
                    </button>
                </div>
            </div>
            {loading &&
                <LoadingUI uploadingFile={true} />
            }
        </>
    );
};

export default FileUploadSection;
